import { useState } from 'react';
import { api } from '../lib/api';
import { haptic } from '../lib/telegram';
import AmountInput from '../components/AmountInput';
import type { Currency } from '../lib/types';
import { parseAmountInput, toDatetimeLocal, datetimeLocalToISO } from '../lib/format';

interface Props {
  onDone: () => void;
  onBack: () => void;
}

type Direction = 'USD_TO_SYP' | 'SYP_TO_USD';

export default function Exchange({ onDone, onBack }: Props) {
  const [direction, setDirection] = useState<Direction>('USD_TO_SYP');
  const [fromAmt, setFromAmt] = useState('');
  const [toAmt, setToAmt] = useState('');
  const [rate, setRate] = useState('');
  const [occurredAt, setOccurredAt] = useState(toDatetimeLocal(new Date()));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const fromCurrency: Currency = direction === 'USD_TO_SYP' ? 'USD' : 'SYP';
  const toCurrency: Currency   = direction === 'USD_TO_SYP' ? 'SYP' : 'USD';

  function flip() {
    setDirection((d) => (d === 'USD_TO_SYP' ? 'SYP_TO_USD' : 'USD_TO_SYP'));
    setFromAmt(toAmt);
    setToAmt(fromAmt);
  }

  // Recompute when from + rate are present: to = from * rate (USD→SYP) or from / rate (SYP→USD).
  function recomputeToFromRate(fromStr: string, rateStr: string) {
    const f = parseAmountInput(fromStr);
    const r = parseAmountInput(rateStr);
    if (f === null || r === null) return;
    const to = direction === 'USD_TO_SYP' ? f * r : f / r;
    setToAmt(direction === 'USD_TO_SYP' ? Math.round(to).toString() : to.toFixed(2));
  }

  // Recompute rate when both amounts known.
  function recomputeRateFromAmounts(fromStr: string, toStr: string) {
    const f = parseAmountInput(fromStr);
    const t = parseAmountInput(toStr);
    if (f === null || t === null) return;
    const r = direction === 'USD_TO_SYP' ? t / f : f / t;
    setRate(r.toFixed(direction === 'USD_TO_SYP' ? 2 : 6));
  }

  async function submit(ev: React.FormEvent) {
    ev.preventDefault();
    const f = parseAmountInput(fromAmt);
    const t = parseAmountInput(toAmt);
    const r = parseAmountInput(rate);
    if (f === null || t === null || r === null) {
      setErr('Fill all three: from amount, to amount, and rate.');
      return;
    }
    setErr(null);
    setBusy(true);
    try {
      await api.exchange({
        from_currency: fromCurrency,
        from_amount: f,
        to_currency: toCurrency,
        to_amount: t,
        rate: r,
        occurred_at: datetimeLocalToISO(occurredAt),
      });
      haptic('success');
      onDone();
    } catch (e) {
      haptic('error');
      setErr(e instanceof Error ? e.message : 'failed to save');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="header">
        <button className="back" onClick={onBack}>← Back</button>
        <h1>Exchange</h1>
      </div>
      {err && <div className="error">{err}</div>}

      <form onSubmit={submit}>
        <div className="card">
          <div className="row">
            <div className="muted small">Direction</div>
            <button type="button" className="btn btn-secondary" style={{ width: 'auto', padding: '8px 14px' }} onClick={flip}>
              {fromCurrency} → {toCurrency}  ⇄
            </button>
          </div>

          <label className="label">From ({fromCurrency})</label>
          <AmountInput
            value={fromAmt}
            currency={fromCurrency}
            onChange={(v) => { setFromAmt(v); recomputeToFromRate(v, rate); }}
            autoFocus
          />

          <label className="label">Rate (1 USD = ? SYP)</label>
          <input
            className="input"
            inputMode="decimal"
            autoComplete="off"
            placeholder="e.g. 15000"
            value={rate}
            onChange={(e) => { setRate(e.target.value); recomputeToFromRate(fromAmt, e.target.value); }}
          />

          <label className="label">To ({toCurrency})</label>
          <AmountInput
            value={toAmt}
            currency={toCurrency}
            onChange={(v) => { setToAmt(v); recomputeRateFromAmounts(fromAmt, v); }}
          />

          <label className="label">Date &amp; time</label>
          <input
            className="input"
            type="datetime-local"
            value={occurredAt}
            onChange={(e) => setOccurredAt(e.target.value)}
          />

          <div className="muted small" style={{ marginTop: 8 }}>
            Tip: enter From + Rate and we fill To, or enter From + To and we fill Rate.
          </div>
        </div>

        <button type="submit" className="btn" disabled={busy}>
          {busy ? 'Saving…' : 'Record exchange'}
        </button>
      </form>
    </>
  );
}
